
import { type ReactNode } from 'react';
import { useTranslations } from '@/i18n/translations';
import {
  Body,
  Box,
  Button,
  Card,
  Eyebrow,
  I,
  Pill,
  Row,
  Skeleton,
  SkeletonText,
  Stack,
  useDelayedLoading,
} from '@leanin/ui';
import { SocialIconLink } from '../social/SocialIconLink';
import { GroupLinkListCard } from '../groups/GroupLinkListCard';
import { GroupPeopleListCard } from '../groups/GroupPeopleListCard';
import { PostCard } from '../feed/PostCard';
import { useIndustryLabel } from '../members';
import { CIRCLE_FORMAT_LABEL_KEY } from '../circles/circle-format';
import type { Profile, ProfileCirclePreferences, ProfileSocialLinks } from './types';

type Props = {
  /** Adapted profile, built from the member identity as soon as it resolves.
   *  The primary About / Get-to-know-me / facts cards render from it
   *  immediately. Secondary regions (posts, aside lists) fill in
   *  independently: a slow or failing collateral query paints that region's
   *  own skeleton and never blocks the primary content. */
  profile: Profile;
  /** Author-posts query still in flight with no rows yet → Activity skeleton. */
  postsLoading?: boolean;
  /** Aside collateral (mutuals for other members; circles + networks for the
   *  self preview) still in flight with no rows yet → aside list skeleton. */
  asideLoading?: boolean;
  /** True when the post-author API still has rows past the current
   *  page. Drives the "Load more" button at the foot of the post list. */
  hasMorePosts?: boolean;
  /** True while the next post page is in flight; disables the button. */
  loadingMorePosts?: boolean;
  /** Click handler for "Load more". The parent owns the pagination
   *  state — clicking just bumps the limit and refetches. */
  onLoadMorePosts?: () => void;
};

export function ProfileContentView({
  profile,
  postsLoading = false,
  asideLoading = false,
  hasMorePosts = false,
  loadingMorePosts = false,
  onLoadMorePosts,
}: Props) {
  const t = useTranslations('Profile');
  const showNetworks = profile.networks.length > 0;
  const showCircles = profile.circles.length > 0;
  const showMutuals = profile.mutuals.length > 0;
  const asideHasContent = showNetworks || showCircles || showMutuals;

  // Per-region skeletons, delayed so a sub-200ms resolve never flashes them.
  // Only shown while the region's own query is in flight with nothing to
  // paint yet — the primary cards above never wait on these.
  const showPostsSkeleton = useDelayedLoading(
    postsLoading && profile.posts.length === 0,
  );
  const showAsideSkeleton = useDelayedLoading(asideLoading && !asideHasContent);

  return (
    <Row
      gap="xl"
      rowGap="xl"
      align="flex-start"
      wrap
      pt="xs"
    >
      <Stack gap="lg" style={{ flex: 1, minWidth: 320 }}>
        <AboutCard profile={profile} />
        <PromptsCard profile={profile} />
        {showPostsSkeleton ? (
          <ActivitySkeleton />
        ) : (
          <ActivityList
            profile={profile}
            hasMorePosts={hasMorePosts}
            loadingMorePosts={loadingMorePosts}
            {...(onLoadMorePosts ? { onLoadMorePosts } : {})}
          />
        )}
      </Stack>

      <Box style={{ width: 320, flexShrink: 0 }}>
        <Stack gap="lg">
          <AboutFactsCard profile={profile} />
          {profile.circlePreferences !== null ? (
            <CirclePreferencesFactsCard preferences={profile.circlePreferences} />
          ) : null}
          {showAsideSkeleton && <AsideListSkeleton />}
          {showNetworks && (
            <GroupLinkListCard
              title={t('sidebar.networks')}
              kind="network"
              groups={profile.networks.map((n) => ({
                id: n.id,
                name: n.name,
                meta: `${n.role === 'Leader' ? t('network.leaderRole') : t('network.memberRole')} · ${t('sidebar.networkMemberCount', { count: n.memberCount })}`,
                ...(n.imageUrl ? { imageUrl: n.imageUrl } : {}),
              }))}
            />
          )}
          {showCircles && (
            <GroupLinkListCard
              title={t('sidebar.circles')}
              kind="circle"
              groups={profile.circles.map((c) => ({
                id: c.id,
                name: c.name,
                meta: `${c.role === 'Leader' ? t('circle.leaderRole') : t('circle.memberRole')} · ${c.meta}`,
                ...(c.imageUrl ? { imageUrl: c.imageUrl } : {}),
              }))}
            />
          )}
          {showMutuals && (
            <GroupPeopleListCard
              title={t('people.otherEyebrow')}
              people={profile.mutuals.map((m) => ({
                id: m.id,
                name: m.name,
                ...(m.photo ? { photo: m.photo } : {}),
              }))}
            />
          )}
        </Stack>
      </Box>
    </Row>
  );
}

// ── About card (left, bio prose) ──────────────────────────────────────────────
//
// Renders the read-only "Bio" panel; suppresses itself when the target
// has no bio set. Editing happens in /settings/account.

function AboutCard({ profile }: { profile: Profile }) {
  const t = useTranslations('Profile');
  if (!profile.about) return null;
  return (
    <Card pad="spacious">
      <Stack gap="lg">
        <Eyebrow tone="accent">{t('about.eyebrow')}</Eyebrow>
        <Body size="md" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>{profile.about}</Body>
      </Stack>
    </Card>
  );
}

// ── About facts card (right rail) ─────────────────────────────────────────────
//
// Structured facts: job title / company / industry, education, and social
// icons. Personal prompts and Open to live in PromptsCard on the left.

type FactKey = 'jobTitle' | 'company' | 'industry' | 'location';

function educationLine(profile: Profile): string | null {
  const parts: string[] = [];
  if (profile.educationDegree && profile.educationField) {
    parts.push(`${profile.educationDegree}, ${profile.educationField}`);
  } else if (profile.educationDegree) {
    parts.push(profile.educationDegree);
  } else if (profile.educationField) {
    parts.push(profile.educationField);
  }
  if (profile.educationSchool) parts.push(profile.educationSchool);
  if (profile.educationGradYear) parts.push(String(profile.educationGradYear));
  return parts.length > 0 ? parts.join(' · ') : null;
}

function AboutFactsCard({ profile }: { profile: Profile }) {
  const t = useTranslations('Profile');
  const industryLabel = useIndustryLabel();
  const facts: ReadonlyArray<{ key: FactKey; value: string | null }> = [
    { key: 'jobTitle', value: profile.jobTitle },
    { key: 'company', value: profile.company },
    {
      key: 'industry',
      value: profile.industryCode === null ? null : industryLabel(profile.industryCode),
    },
    { key: 'location', value: profile.location },
  ];
  const filled = facts.filter((f): f is typeof f & { value: string } =>
    typeof f.value === 'string' && f.value.trim().length > 0,
  );
  const education = educationLine(profile);
  const socials = profile.socialLinks;
  const hasSocials =
    socials !== null &&
    (Boolean(socials.linkedin) ||
      Boolean(socials.twitter) ||
      Boolean(socials.instagram) ||
      Boolean(socials.website));

  if (filled.length === 0 && !education && !hasSocials) return null;

  return (
    <Card pad="spacious">
      <Stack gap="lg">
        <Eyebrow tone="accent">{t('sidebar.about')}</Eyebrow>
        {filled.map((f) => (
          <Stack key={f.key} gap="xs">
            <Eyebrow>{t(`facts.${f.key}`)}</Eyebrow>
            <Body size="md">{f.value}</Body>
          </Stack>
        ))}
        {education !== null ? (
          <Stack gap="xs">
            <Eyebrow>{t('facts.education')}</Eyebrow>
            <Body size="md">{education}</Body>
          </Stack>
        ) : null}
        {hasSocials ? <SocialIconRow links={socials} /> : null}
      </Stack>
    </Card>
  );
}

// ── Circle preferences card (right rail, own profile only) ────────────────────
//
// What the member gave in their Circle preferences: how they would like to
// meet and their interests. Only their own profile carries these; suppressed
// when they gave none.

function CirclePreferencesFactsCard({ preferences }: { preferences: ProfileCirclePreferences }) {
  const t = useTranslations('Profile');
  const tc = useTranslations('Circles');
  const industryLabel = useIndustryLabel();
  const interests = [...preferences.industryCodes.map(industryLabel), ...preferences.tags];

  if (preferences.formats.length === 0 && interests.length === 0) return null;

  return (
    <Card pad="spacious">
      <Stack gap="lg">
        <Eyebrow tone="accent">{t('circlePreferences.title')}</Eyebrow>
        {preferences.formats.length > 0 ? (
          <Stack gap="xs">
            <Eyebrow>{t('circlePreferences.formats')}</Eyebrow>
            <Body size="md">{preferences.formats.map((format) => tc(CIRCLE_FORMAT_LABEL_KEY[format])).join(', ')}</Body>
          </Stack>
        ) : null}
        {interests.length > 0 ? (
          <Stack gap="sm">
            <Eyebrow>{t('circlePreferences.interests')}</Eyebrow>
            <Row gap="sm" wrap>
              {interests.map((interest) => (
                <Pill key={interest} size="sm">
                  {interest}
                </Pill>
              ))}
            </Row>
          </Stack>
        ) : null}
      </Stack>
    </Card>
  );
}

// ── Prompts card (left, below bio) ────────────────────────────────────────────
//
// Personal prompts (ask me about, looking to learn, surprise fact) and Open to
// connection preferences. Suppressed when all fields are empty.

type PromptKey = keyof Pick<Profile, 'askMeAbout' | 'lookingToLearn' | 'surpriseFact'>;

const PROMPT_FIELDS: ReadonlyArray<{ key: PromptKey }> = [
  { key: 'askMeAbout' },
  { key: 'lookingToLearn' },
  { key: 'surpriseFact' },
];

function PromptsCard({ profile }: { profile: Profile }) {
  const t = useTranslations('Profile');
  const prompts = PROMPT_FIELDS
    .map((f) => ({ ...f, value: profile[f.key] }))
    .filter((f): f is typeof f & { value: string } =>
      typeof f.value === 'string' && f.value.trim().length > 0,
    );
  const openTo = profile.openTo.length > 0 ? profile.openTo : null;

  if (prompts.length === 0 && !openTo) return null;

  return (
    <Card pad="spacious">
      <Stack gap="lg">
        <Eyebrow tone="accent">{t('about.getToKnowMe')}</Eyebrow>
        {openTo !== null ? (
          <Stack gap="xs">
            <Eyebrow>{t('prompts.openTo')}</Eyebrow>
            <Body size="md">{openTo.join(' · ')}</Body>
          </Stack>
        ) : null}
        {prompts.map((f) => (
          <Stack key={f.key} gap="xs">
            <Eyebrow>{t(`prompts.${f.key}`)}</Eyebrow>
            <Body size="md">{f.value}</Body>
          </Stack>
        ))}
      </Stack>
    </Card>
  );
}

// One horizontal row of clickable social icons. Order: LinkedIn, X,
// Instagram, Website — same order as the underlying schema fields.
const SOCIAL_ICONS: ReadonlyArray<{
  key: keyof ProfileSocialLinks;
  glyph: ReactNode;
}> = [
  { key: 'linkedin',  glyph: I.linkedin },
  { key: 'twitter',   glyph: I.xBrand },
  { key: 'instagram', glyph: I.instagram },
  { key: 'website',   glyph: I.globe },
];

function socialHref(handle: string): string {
  const stripped = handle.replace(/^https?:\/\//i, '');
  return `https://${stripped}`;
}

// Renders a horizontal row of clickable social icons. The visual styling
// (40px circle, --muted background, --muted-foreground glyph) lives in
// the shared `SocialIconLink` primitive so it stays in lockstep with the
// group surfaces (`GroupSocialLinksCard`).
function SocialIconRow({ links }: { links: NonNullable<Profile['socialLinks']> }) {
  const t = useTranslations('Profile');
  return (
    <Row gap="sm" align="center" wrap>
      {SOCIAL_ICONS.map((s) => {
        const handle = links[s.key];
        if (!handle) return null;
        return (
          <SocialIconLink
            key={s.key}
            href={socialHref(handle)}
            label={t(`social.${s.key}`)}
            glyph={s.glyph}
          />
        );
      })}
    </Row>
  );
}

// ── Activity list ─────────────────────────────────────────────────────────────

function ActivityList({
  profile,
  hasMorePosts,
  loadingMorePosts,
  onLoadMorePosts,
}: {
  profile: Profile;
  hasMorePosts: boolean;
  loadingMorePosts: boolean;
  onLoadMorePosts?: () => void;
}) {
  const t = useTranslations('Profile');
  if (profile.posts.length === 0) return null;
  return (
    <Stack gap="lg">
      <Eyebrow tone="accent">
        {t('activity.otherTitle', { firstName: firstName(profile.name) })}
      </Eyebrow>
      {profile.posts.map((post) => (
        <PostCard key={post.id} post={post} />
      ))}
      {hasMorePosts && onLoadMorePosts ? (
        <Row justify="center">
          <Button
            variant="ghost"
            size="sm"
            loading={loadingMorePosts}
            onClick={onLoadMorePosts}
          >
            {t('activity.loadMore')}
          </Button>
        </Row>
      ) : null}
    </Stack>
  );
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

// ── Region skeletons ──────────────────────────────────────────────────────────
//
// Shown only while a secondary region's query is still in flight (and has
// nothing to paint yet). The primary About / facts / prompts cards always
// render from the resolved member identity, so a slow or failing collateral
// query never gates the whole profile.

// Full body placeholder for the phase BEFORE the member identity resolves —
// when there is nothing at all to paint yet. Used by `ProfileChromeSkeleton`.
// Once the member resolves, `ProfileContentView` takes over and only the
// per-region skeletons below remain in play.
export function ProfileBodySkeleton() {
  return (
    <Row gap="xl" rowGap="xl" align="flex-start" wrap pt="xs">
      <Stack gap="lg" style={{ flex: 1, minWidth: 320 }}>
        <Card pad="spacious">
          <Stack gap="lg">
            <Skeleton width={80} height={12} rounded="sm" />
            <SkeletonText lines={3} size="md" />
          </Stack>
        </Card>
        <ActivitySkeleton />
      </Stack>
      <Box style={{ width: 320, flexShrink: 0 }}>
        <Stack gap="lg">
          <AsideListSkeleton />
          <AsideListSkeleton />
        </Stack>
      </Box>
    </Row>
  );
}

function ActivitySkeleton() {
  return (
    <Stack gap="lg">
      <Skeleton width={200} height={12} rounded="sm" />
      {Array.from({ length: 2 }, (_, i) => (
        <Card key={i} pad="none" style={{ overflow: 'hidden' }}>
          <Stack gap="lg" p="xl">
            <Row gap="md" align="center">
              <Skeleton width={44} height={44} rounded="full" style={{ flexShrink: 0 }} />
              <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                <Skeleton width="45%" height={15} rounded="sm" />
                <Row gap="sm" align="center">
                  <Skeleton width={90} height={24} rounded="full" />
                  <Skeleton width={60} height={13} rounded="sm" />
                </Row>
              </Stack>
            </Row>
            <SkeletonText lines={3} size="md" />
          </Stack>
          <Row
            py="md"
            px="xl"
            align="center"
            justify="space-between"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <Row gap="sm" align="center">
              <Skeleton width={56} height={28} rounded="md" />
              <Skeleton width={96} height={28} rounded="md" />
            </Row>
            <Row gap="xs" align="center">
              <Skeleton width={28} height={28} rounded="md" />
              <Skeleton width={28} height={28} rounded="md" />
            </Row>
          </Row>
        </Card>
      ))}
    </Stack>
  );
}

function AsideListSkeleton() {
  return (
    <Card pad="default">
      <Stack gap="md">
        <Skeleton width={92} height={28} rounded="sm" />
        {Array.from({ length: 3 }, (_, i) => (
          <Row key={i} gap="md" align="center">
            <Skeleton width={44} height={44} rounded="sm" style={{ flexShrink: 0 }} />
            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              <Skeleton width="60%" height={14} rounded="sm" />
              <Skeleton width="48%" height={13} rounded="sm" />
            </Stack>
            <Skeleton width={10} height={14} rounded="sm" style={{ flexShrink: 0 }} />
          </Row>
        ))}
      </Stack>
    </Card>
  );
}
